import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Calendar,
  Download,
  RefreshCw,
  Filter,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Package,
  AlertCircle,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  BarChart3,
  Globe,
  Users,
  Building2,
  FileText,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info,
  X,
  Search
} from 'lucide-react';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import LoadingSpinner from '../../../shared/components/LoadingSpinner';

// Chart colors
const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#14b8a6'];
const VARIANCE_COLORS = {
  duty: '#ef4444',
  tariff: '#f59e0b',
  freight: '#3b82f6'
};

/**
 * Cost Allocation Report with Date Range Filter, Pivot Table, and Charts
 */
export default function CostAllocationDateReport({ onBackToDashboard, embedded = false }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);

  // Date range filters
  const [startDate, setStartDate] = useState('2025-07-01');
  const [endDate, setEndDate] = useState('2025-11-30');

  // Post-load filters (filter data after it's loaded) - arrays for multi-select
  const [postFilters, setPostFilters] = useState({
    division: [],
    customer: [],
    status: [],
    country: []
  });

  // Pivot filters (dropdowns in pivot header) - arrays for multi-select
  const [pivotFilters, setPivotFilters] = useState({
    customer: [],
    office: [],
    country: [],
    terms: [],
    division: []
  });

  // Dropdown open states
  const [openDropdown, setOpenDropdown] = useState(null);
  const containerRef = useRef(null);

  // PDF Export state and refs
  const [exportingPdf, setExportingPdf] = useState(false);
  const chartsRef = useRef(null);

  // Missing HTS Data modal state
  const [showMissingHTSModal, setShowMissingHTSModal] = useState(false);
  const [missingHTSData, setMissingHTSData] = useState([]);
  const [missingHTSSummary, setMissingHTSSummary] = useState(null);
  const [missingHTSLoading, setMissingHTSLoading] = useState(false);
  const [missingHTSError, setMissingHTSError] = useState(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Selected month from pivot (filters charts and details)
  const [selectedMonth, setSelectedMonth] = useState(null);

  // Selected customer from chart or table (filters detail data)
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Expanded states for hierarchical detail table
  const [expandedInvoices, setExpandedInvoices] = useState(new Set());
  const [expandedPOs, setExpandedPOs] = useState(new Set());

  // Customer table sorting
  const [customerSort, setCustomerSort] = useState({ key: 'freight_difference', direction: 'asc' });

  // Section visibility
  const [showCharts, setShowCharts] = useState(true);
  const [showPivot, setShowPivot] = useState(true);
  const [showDetail, setShowDetail] = useState(true);

  // Get unique values for dropdowns
  const dropdownOptions = useMemo(() => {
    if (!data.length) return { divisions: [], customers: [], statuses: [], countries: [], terms: [], offices: [] };

    const divisions = [...new Set(data.map(r => r.division_name).filter(Boolean))].sort();
    const customers = [...new Set(data.map(r => r.customer).filter(Boolean))].sort();
    const statuses = [...new Set(data.map(r => r.data_completeness_status).filter(Boolean))].sort();
    const countries = [...new Set(data.map(r => r.country_of_origin).filter(Boolean))].sort();
    const terms = [...new Set(data.map(r => r.terms2).filter(Boolean))].sort();
    const offices = [...new Set(data.map(r => r.office).filter(Boolean))].sort();

    return { divisions, customers, statuses, countries, terms, offices };
  }, [data]);

  // Apply post-load filters (multi-select arrays)
  const filteredData = useMemo(() => {
    let result = data;

    if (postFilters.division.length > 0) {
      result = result.filter(r => postFilters.division.includes(r.division_name));
    }
    if (postFilters.customer.length > 0) {
      result = result.filter(r => postFilters.customer.includes(r.customer));
    }
    if (postFilters.status.length > 0) {
      result = result.filter(r => postFilters.status.includes(r.data_completeness_status));
    }
    if (postFilters.country.length > 0) {
      result = result.filter(r => postFilters.country.includes(r.country_of_origin));
    }

    return result;
  }, [data, postFilters]);

  // Apply pivot filters (multi-select arrays)
  const pivotFilteredData = useMemo(() => {
    let result = filteredData;

    if (pivotFilters.customer.length > 0) {
      result = result.filter(r => pivotFilters.customer.includes(r.customer));
    }
    if (pivotFilters.office.length > 0) {
      result = result.filter(r => pivotFilters.office.includes(r.office));
    }
    if (pivotFilters.country.length > 0) {
      result = result.filter(r => pivotFilters.country.includes(r.country_of_origin));
    }
    if (pivotFilters.terms.length > 0) {
      result = result.filter(r => pivotFilters.terms.includes(r.terms2));
    }
    if (pivotFilters.division.length > 0) {
      result = result.filter(r => pivotFilters.division.includes(r.division_name));
    }

    return result;
  }, [filteredData, pivotFilters]);

  // Data filtered by selected month (for charts and details) - use last_receipt_month
  const monthFilteredData = useMemo(() => {
    if (!selectedMonth) return pivotFilteredData;
    return pivotFilteredData.filter(r => r.last_receipt_month === selectedMonth);
  }, [pivotFilteredData, selectedMonth]);

  // Pivot data (always grouped by last_receipt_month)
  const pivotData = useMemo(() => {
    if (!pivotFilteredData.length) return [];

    const grouped = {};

    pivotFilteredData.forEach(row => {
      const month = row.last_receipt_month || 'Unknown';

      if (!grouped[month]) {
        grouped[month] = {
          month,
          total_fob: 0,
          total_line_entered_value: 0,
          tariff_difference: 0,
          duty_difference: 0,
          freight_difference: 0,
          count: 0
        };
      }

      grouped[month].total_fob += parseFloat(row.total_fob) || 0;
      grouped[month].total_line_entered_value += parseFloat(row.total_line_entered_value) || 0;
      grouped[month].tariff_difference += parseFloat(row.tariff_difference) || 0;
      grouped[month].duty_difference += parseFloat(row.duty_difference) || 0;
      grouped[month].freight_difference += parseFloat(row.freight_difference) || 0;
      grouped[month].count += 1;
    });

    return Object.values(grouped).sort((a, b) => a.month.localeCompare(b.month));
  }, [pivotFilteredData]);

  // Customer pivot data (grouped by customer instead of month)
  const customerPivotData = useMemo(() => {
    const dataSource = selectedMonth ? monthFilteredData : pivotFilteredData;
    if (!dataSource.length) return [];

    const grouped = {};

    dataSource.forEach(row => {
      const customer = row.customer || 'Unknown';

      if (!grouped[customer]) {
        grouped[customer] = {
          customer,
          total_fob: 0,
          total_line_entered_value: 0,
          tariff_difference: 0,
          duty_difference: 0,
          freight_difference: 0,
          count: 0
        };
      }

      grouped[customer].total_fob += parseFloat(row.total_fob) || 0;
      grouped[customer].total_line_entered_value += parseFloat(row.total_line_entered_value) || 0;
      grouped[customer].tariff_difference += parseFloat(row.tariff_difference) || 0;
      grouped[customer].duty_difference += parseFloat(row.duty_difference) || 0;
      grouped[customer].freight_difference += parseFloat(row.freight_difference) || 0;
      grouped[customer].count += 1;
    });

    // Sort based on customerSort state
    const values = Object.values(grouped);
    return values.sort((a, b) => {
      let aVal, bVal;
      if (customerSort.key === 'total_variance') {
        aVal = a.tariff_difference + a.duty_difference + a.freight_difference;
        bVal = b.tariff_difference + b.duty_difference + b.freight_difference;
      } else if (customerSort.key === 'customer') {
        aVal = a.customer.toLowerCase();
        bVal = b.customer.toLowerCase();
        return customerSort.direction === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      } else {
        aVal = a[customerSort.key] || 0;
        bVal = b[customerSort.key] || 0;
      }
      return customerSort.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [pivotFilteredData, monthFilteredData, selectedMonth, customerSort]);

  // Grand totals for pivot (respects month filter)
  const grandTotals = useMemo(() => {
    const dataSource = selectedMonth ? monthFilteredData : pivotFilteredData;
    return dataSource.reduce((acc, row) => ({
      total_fob: acc.total_fob + row.total_fob,
      total_line_entered_value: acc.total_line_entered_value + row.total_line_entered_value,
      tariff_difference: acc.tariff_difference + row.tariff_difference,
      duty_difference: acc.duty_difference + row.duty_difference,
      freight_difference: acc.freight_difference + row.freight_difference,
      count: acc.count + row.count
    }), {
      total_fob: 0,
      total_line_entered_value: 0,
      tariff_difference: 0,
      duty_difference: 0,
      freight_difference: 0,
      count: 0
    });
  }, [pivotFilteredData, monthFilteredData, selectedMonth]);

  // Summary stats (Total FOB, Duty Saving, Tariff Saving, Freight Saving)
  const stats = useMemo(() => {
    const dataToUse = selectedMonth ? monthFilteredData : pivotFilteredData;

    return {
      totalFob: dataToUse.reduce((sum, r) => sum + (parseFloat(r.total_fob) || 0), 0),
      dutySaving: dataToUse.reduce((sum, r) => sum + (parseFloat(r.duty_difference) || 0), 0),
      tariffSaving: dataToUse.reduce((sum, r) => sum + (parseFloat(r.tariff_difference) || 0), 0),
      freightSaving: dataToUse.reduce((sum, r) => sum + (parseFloat(r.freight_difference) || 0), 0)
    };
  }, [pivotFilteredData, monthFilteredData, selectedMonth]);

  // Completeness summary stats
  const completenessStats = useMemo(() => {
    const dataToUse = selectedMonth ? monthFilteredData : pivotFilteredData;
    const invoices = [...new Set(dataToUse.map(r => r.commercial_invoice))];
    const statusCounts = {};

    dataToUse.forEach(row => {
      const status = row.data_completeness_status || 'Unknown';
      if (!statusCounts[status]) statusCounts[status] = new Set();
      statusCounts[status].add(row.commercial_invoice);
    });

    const completeInvoices = statusCounts['COMPLETE DATA']?.size || 0;
    const totalInvoices = invoices.length;

    return {
      totalInvoices,
      completeInvoices,
      incompleteInvoices: totalInvoices - completeInvoices,
      completionRate: totalInvoices > 0 ? Math.round((completeInvoices / totalInvoices) * 100) : 0,
      statusBreakdown: Object.entries(statusCounts).map(([status, set]) => ({ status, count: set.size }))
    };
  }, [pivotFilteredData, monthFilteredData, selectedMonth]);

  // FOB by Customer bar chart data
  const fobByCustomerData = useMemo(() => {
    const dataToUse = selectedMonth ? monthFilteredData : pivotFilteredData;
    const grouped = {};

    dataToUse.forEach(row => {
      const customer = row.customer || 'Unknown';
      if (!grouped[customer]) grouped[customer] = 0;
      grouped[customer] += parseFloat(row.total_fob) || 0;
    });

    return Object.entries(grouped)
      .map(([name, value]) => ({
        name: name.length > 12 ? name.substring(0, 12) + '..' : name,
        fullName: name,
        value: Math.round(value)
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 12);
  }, [pivotFilteredData, monthFilteredData, selectedMonth]);

  // Multi-select dropdown toggle handler
  const toggleMultiSelect = (filterType, filterGroup, value) => {
    const setter = filterGroup === 'post' ? setPostFilters : setPivotFilters;
    setter(prev => {
      const current = prev[filterType] || [];
      if (current.includes(value)) {
        return { ...prev, [filterType]: current.filter(v => v !== value) };
      } else {
        return { ...prev, [filterType]: [...current, value] };
      }
    });
  };

  const clearFilter = (filterType, filterGroup) => {
    const setter = filterGroup === 'post' ? setPostFilters : setPivotFilters;
    setter(prev => ({ ...prev, [filterType]: [] }));
  };

  // Multi-select dropdown component
  const MultiSelectDropdown = ({ label, icon: Icon, options, selected, filterType, filterGroup }) => {
    const isOpen = openDropdown === `${filterGroup}-${filterType}`;
    const dropdownId = `${filterGroup}-${filterType}`;
    
    return (
      <div className="relative">
        <label className="block text-xs font-medium text-gray-700 mb-1">
          {Icon && <Icon size={14} className="inline mr-1" />}
          {label}
        </label>
        <button
          type="button"
          onClick={() => setOpenDropdown(isOpen ? null : dropdownId)}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-left flex items-center justify-between"
        >
          <span className={selected.length === 0 ? 'text-gray-400' : 'text-gray-900'}>
            {selected.length === 0 ? `All ${label}` : `${selected.length} selected`}
          </span>
          <ChevronDown size={14} className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        
        {isOpen && (
          <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            <div className="sticky top-0 bg-gray-50 border-b border-gray-200 px-3 py-2 flex justify-between items-center">
              <span className="text-xs font-medium text-gray-600">{selected.length} selected</span>
              <button
                onClick={(e) => { e.stopPropagation(); clearFilter(filterType, filterGroup); }}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Clear
              </button>
            </div>
            {options.map(option => (
              <label
                key={option}
                className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(option)}
                  onChange={() => toggleMultiSelect(filterType, filterGroup, option)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="text-gray-700 truncate">{option}</span>
              </label>
            ))}
            {options.length === 0 && (
              <div className="px-3 py-4 text-sm text-gray-400 text-center">No options available</div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Variance stacked bar data (by month) - use actual values with sign
  const varianceStackedData = useMemo(() => {
    return pivotData.map(row => ({
      month: row.month,
      'Duty Diff': row.duty_difference,
      'Tariff Diff': row.tariff_difference,
      'Freight Diff': row.freight_difference
    }));
  }, [pivotData]);

  // Customer impact data
  const customerImpactData = useMemo(() => {
    const dataToUse = selectedMonth ? monthFilteredData : pivotFilteredData;
    const grouped = {};

    dataToUse.forEach(row => {
      const customer = row.customer || 'Unknown';
      if (!grouped[customer]) grouped[customer] = 0;
      grouped[customer] += (parseFloat(row.duty_difference) || 0) +
        (parseFloat(row.tariff_difference) || 0) +
        (parseFloat(row.freight_difference) || 0);
    });

    return Object.entries(grouped)
      .map(([name, value]) => ({
        name: name.length > 15 ? name.substring(0, 15) + '...' : name,
        value: Math.round(value)
      }))
      .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
      .slice(0, 10);
  }, [pivotFilteredData, monthFilteredData, selectedMonth]);

  // Hierarchical detail data (Invoice → PO → Style) with completeness info
  const hierarchicalData = useMemo(() => {
    let dataToUse = selectedMonth ? monthFilteredData : pivotFilteredData;
    if (selectedCustomer) {
      dataToUse = dataToUse.filter(r => (r.customer || 'Unknown') === selectedCustomer);
    }
    const invoiceMap = new Map();

    dataToUse.forEach(row => {
      const invoice = row.commercial_invoice || 'Unknown';
      const po = row.production_number || 'Unknown';

      if (!invoiceMap.has(invoice)) {
        invoiceMap.set(invoice, {
          invoice,
          customer: row.customer || 'Unknown',
          division: row.division_name || row.division || '',
          pos: new Map(),
          totals: { qty: 0, fob: 0, duty: 0, tariff: 0, freight: 0 },
          completeness: {
            statuses: new Set(),
            hasDuty: false,
            hasFreight: false,
            hasHTS: false,
            hasShipping: false,
            htsRemarks: new Set(),
            costRemarks: new Set()
          }
        });
      }

      const invoiceData = invoiceMap.get(invoice);

      // Track completeness
      if (row.data_completeness_status) {
        invoiceData.completeness.statuses.add(row.data_completeness_status);
      }
      if (row.hts_matching_remark) {
        invoiceData.completeness.htsRemarks.add(row.hts_matching_remark);
      }
      if (row.component_cost_remark) {
        invoiceData.completeness.costRemarks.add(row.component_cost_remark);
      }

      // Check data presence
      if (row.duty_difference !== null && row.duty_difference !== undefined) {
        invoiceData.completeness.hasDuty = true;
      }
      if (row.freight_difference !== null && row.freight_difference !== undefined && row.freight_cost_per_unit > 0) {
        invoiceData.completeness.hasFreight = true;
      }
      if (row.data_completeness_status !== 'MISSING DUTY DATA') {
        invoiceData.completeness.hasHTS = true;
      }
      if (row.data_completeness_status !== 'MISSING FREIGHT DATA') {
        invoiceData.completeness.hasShipping = true;
      }

      if (!invoiceData.pos.has(po)) {
        invoiceData.pos.set(po, {
          po,
          styles: [],
          totals: { qty: 0, fob: 0, duty: 0, tariff: 0, freight: 0 }
        });
      }

      const poData = invoiceData.pos.get(po);
      const qty = parseFloat(row.qty) || 0;
      const fob = parseFloat(row.total_fob) || 0;
      const duty = parseFloat(row.duty_difference) || 0;
      const tariff = parseFloat(row.tariff_difference) || 0;
      const freight = parseFloat(row.freight_difference) || 0;

      poData.styles.push({
        style: row.style,
        color: row.color,
        qty,
        fob,
        duty,
        tariff,
        freight,
        warehouse: row.warehouse,
        country: row.country_of_origin,
        status: row.data_completeness_status,
        htsRemark: row.hts_matching_remark,
        costRemark: row.component_cost_remark
      });

      poData.totals.qty += qty;
      poData.totals.fob += fob;
      poData.totals.duty += duty;
      poData.totals.tariff += tariff;
      poData.totals.freight += freight;

      invoiceData.totals.qty += qty;
      invoiceData.totals.fob += fob;
      invoiceData.totals.duty += duty;
      invoiceData.totals.tariff += tariff;
      invoiceData.totals.freight += freight;
    });

    return Array.from(invoiceMap.values()).map(inv => ({
      ...inv,
      pos: Array.from(inv.pos.values()),
      completeness: {
        ...inv.completeness,
        statuses: Array.from(inv.completeness.statuses),
        htsRemarks: Array.from(inv.completeness.htsRemarks),
        costRemarks: Array.from(inv.completeness.costRemarks),
        isComplete: inv.completeness.statuses.has('COMPLETE DATA') && inv.completeness.statuses.size === 1
      }
    }));
  }, [pivotFilteredData, monthFilteredData, selectedMonth, selectedCustomer]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setSelectedMonth(null);
      setSelectedCustomer(null);
      setExpandedInvoices(new Set());
      setExpandedPOs(new Set());

      const functions = getFunctions();
      const getCostAllocationWithDates = httpsCallable(functions, 'getCostAllocationWithDates', { timeout: 330000 });

      const result = await getCostAllocationWithDates({ startDate, endDate });
      const records = result.data?.data || result.data || [];
      setData(records);
      console.log(`✅ Loaded ${records.length} records`);
    } catch (err) {
      console.error('❌ Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  // Fetch missing HTS data for diagnostic modal
  const fetchMissingHTSData = useCallback(async () => {
    setMissingHTSLoading(true);
    setMissingHTSError(null);
    try {
      const functions = getFunctions();
      const getMissingHTS = httpsCallable(functions, 'getMissingHTSData', { timeout: 150000 });
      const result = await getMissingHTS({ startDate, endDate });
      const responseData = result.data || {};
      setMissingHTSData(responseData.data || []);
      setMissingHTSSummary(responseData.summary || null);
    } catch (err) {
      console.error('❌ Error fetching missing HTS data:', err);
      setMissingHTSError(err.message);
    } finally {
      setMissingHTSLoading(false);
    }
  }, [startDate, endDate]);

  // Export missing HTS data to Excel
  const exportMissingHTSExcel = () => {
    const exportData = missingHTSData.map(row => ({
      'PO Number': row.prod_num,
      'Commercial Invoice': row.comm_inv,
      'Style': row.sty,
      'Color': row.clr,
      'Label': row.lbl,
      'Division': row.div,
      'Warehouse': row.shp2,
      'Date': row.trans_date,
      'Product Type': row.pod_PrdTyp
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Missing HTS Data');
    XLSX.writeFile(wb, `Missing_HTS_Data_${startDate}_to_${endDate}.xlsx`);
  };

  const toggleInvoice = (invoice) => {
    setExpandedInvoices(prev => {
      const next = new Set(prev);
      if (next.has(invoice)) next.delete(invoice);
      else next.add(invoice);
      return next;
    });
  };

  const togglePO = (key) => {
    setExpandedPOs(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Export pivot to Excel
  const exportPivotToExcel = () => {
    const wb = XLSX.utils.book_new();

    const pivotSheet = XLSX.utils.json_to_sheet([
      ...pivotData.map(row => ({
        'Month': row.month,
        'Sum of FOB': row.total_fob,
        'Sum of Custom Value': row.total_line_entered_value,
        'Tariff Difference': row.tariff_difference,
        'Duty Difference': row.duty_difference,
        'Freight Difference': row.freight_difference,
        'Total Variance': row.tariff_difference + row.duty_difference + row.freight_difference,
        'Records': row.count
      })),
      {
        'Month': 'Grand Total',
        'Sum of FOB': grandTotals.total_fob,
        'Sum of Custom Value': grandTotals.total_line_entered_value,
        'Tariff Difference': grandTotals.tariff_difference,
        'Duty Difference': grandTotals.duty_difference,
        'Freight Difference': grandTotals.freight_difference,
        'Total Variance': grandTotals.tariff_difference + grandTotals.duty_difference + grandTotals.freight_difference,
        'Records': grandTotals.count
      }
    ]);

    XLSX.utils.book_append_sheet(wb, pivotSheet, 'Pivot Summary');
    XLSX.writeFile(wb, `Cost_Allocation_Pivot_${startDate}_to_${endDate}.xlsx`);
  };

  // Export detail to Excel
  const exportDetailToExcel = () => {
    const dataToExport = selectedMonth ? monthFilteredData : pivotFilteredData;
    const wb = XLSX.utils.book_new();
    const detailSheet = XLSX.utils.json_to_sheet(dataToExport);
    XLSX.utils.book_append_sheet(wb, detailSheet, 'Detail Data');
    XLSX.writeFile(wb, `Cost_Allocation_Detail_${startDate}_to_${endDate}.xlsx`);
  };

  // Professional PDF Export Function
  const exportToPdf = async () => {
    if (exportingPdf) return;

    setExportingPdf(true);

    try {
      // Create PDF in landscape for better chart display
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      let yPosition = margin;

      // Get selected divisions for header
      const selectedDivisions = postFilters.division.length > 0
        ? postFilters.division.join(', ')
        : 'All Divisions';

      // Helper function to add header with division info
      const addHeader = () => {
        pdf.setFillColor(30, 58, 138); // Dark blue
        pdf.rect(0, 0, pageWidth, 18, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Cost Allocation Report', margin, 11);

        // Division info in header
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        const divisionText = `Division: ${selectedDivisions}`;
        pdf.text(divisionText, pageWidth / 2 - pdf.getTextWidth(divisionText) / 2, 11);

        // Date range on right
        pdf.text(`${startDate} to ${endDate}`, pageWidth - margin - 45, 11);
        pdf.setTextColor(0, 0, 0);
        return 22;
      };

      // Helper function to add footer
      const addFooter = () => {
        pdf.setFontSize(7);
        pdf.setTextColor(100, 116, 139);
        pdf.text(`Generated: ${new Date().toLocaleString()}`, margin, pageHeight - 4);
        pdf.text('Page 1 of 1', pageWidth - margin - 15, pageHeight - 4);
        pdf.setTextColor(0, 0, 0);
      };

      // Single Page Layout
      yPosition = addHeader();

      // Summary Stats Row (compact)
      const statsBoxWidth = (pageWidth - margin * 2 - 9) / 4;
      const statsBoxHeight = 18;

      // Stats data - positive = green, negative = red
      const summaryStats = [
        { label: 'Total FOB', value: formatCurrency(stats.totalFob), color: [59, 130, 246] },
        { label: 'Duty Variance', value: formatCurrency(stats.dutySaving), color: stats.dutySaving >= 0 ? [16, 185, 129] : [239, 68, 68] },
        { label: 'Tariff Variance', value: formatCurrency(stats.tariffSaving), color: stats.tariffSaving >= 0 ? [16, 185, 129] : [239, 68, 68] },
        { label: 'Freight Variance', value: formatCurrency(stats.freightSaving), color: stats.freightSaving >= 0 ? [16, 185, 129] : [239, 68, 68] }
      ];

      // Compact stats boxes
      summaryStats.forEach((stat, index) => {
        const x = margin + (statsBoxWidth + 3) * index;
        pdf.setFillColor(248, 250, 252);
        pdf.roundedRect(x, yPosition, statsBoxWidth, statsBoxHeight, 2, 2, 'F');
        pdf.setDrawColor(226, 232, 240);
        pdf.roundedRect(x, yPosition, statsBoxWidth, statsBoxHeight, 2, 2, 'S');

        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 116, 139);
        pdf.text(stat.label, x + 3, yPosition + 6);

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(...stat.color);
        pdf.text(stat.value, x + 3, yPosition + 14);
      });

      yPosition += statsBoxHeight + 4;

      // ===== PIVOT TABLE (Full Width) =====
      const tableWidth = pageWidth - margin * 2;

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(30, 41, 59);
      pdf.text('Monthly Summary', margin, yPosition);
      yPosition += 5;

      // Table headers
      const tableHeaders = ['Month', 'Sum of FOB', 'Custom Value', 'Tariff Diff', 'Duty Diff', 'Freight Diff', 'Total Variance'];
      const colWidths = [32, 38, 38, 35, 35, 35, 38];

      // Header row
      pdf.setFillColor(241, 245, 249);
      pdf.rect(margin, yPosition, tableWidth, 6, 'F');
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(71, 85, 105);

      let xPos = margin + 2;
      tableHeaders.forEach((header, i) => {
        pdf.text(header, xPos, yPosition + 4);
        xPos += colWidths[i];
      });
      yPosition += 8;

      // Table data rows (compact - 5mm per row)
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'normal');
      pivotData.forEach((row, rowIndex) => {
        const totalVariance = row.tariff_difference + row.duty_difference + row.freight_difference;

        // Alternate row colors - draw background first, then text
        if (rowIndex % 2 === 1) {
          pdf.setFillColor(249, 250, 251);
          pdf.rect(margin, yPosition, tableWidth, 5, 'F');
        }

        xPos = margin + 2;
        pdf.setTextColor(30, 41, 59);
        pdf.text(row.month, xPos, yPosition + 3.5);
        xPos += colWidths[0];

        pdf.text(formatCurrency(row.total_fob), xPos, yPosition + 3.5);
        xPos += colWidths[1];

        pdf.text(formatCurrency(row.total_line_entered_value), xPos, yPosition + 3.5);
        xPos += colWidths[2];

        // Positive = green, Negative = red
        pdf.setTextColor(row.tariff_difference >= 0 ? 16 : 239, row.tariff_difference >= 0 ? 185 : 68, row.tariff_difference >= 0 ? 129 : 68);
        pdf.text(formatCurrency(row.tariff_difference), xPos, yPosition + 3.5);
        xPos += colWidths[3];

        pdf.setTextColor(row.duty_difference >= 0 ? 16 : 239, row.duty_difference >= 0 ? 185 : 68, row.duty_difference >= 0 ? 129 : 68);
        pdf.text(formatCurrency(row.duty_difference), xPos, yPosition + 3.5);
        xPos += colWidths[4];

        pdf.setTextColor(row.freight_difference >= 0 ? 16 : 239, row.freight_difference >= 0 ? 185 : 68, row.freight_difference >= 0 ? 129 : 68);
        pdf.text(formatCurrency(row.freight_difference), xPos, yPosition + 3.5);
        xPos += colWidths[5];

        pdf.setTextColor(totalVariance >= 0 ? 16 : 239, totalVariance >= 0 ? 185 : 68, totalVariance >= 0 ? 129 : 68);
        pdf.setFont('helvetica', 'bold');
        pdf.text(formatCurrency(totalVariance), xPos, yPosition + 3.5);
        pdf.setFont('helvetica', 'normal');

        yPosition += 5;
      });

      // Grand Total row
      const grandTotal = grandTotals.tariff_difference + grandTotals.duty_difference + grandTotals.freight_difference;
      pdf.setFillColor(226, 232, 240);
      pdf.rect(margin, yPosition, tableWidth, 5.5, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(7);
      pdf.setTextColor(30, 41, 59);

      xPos = margin + 2;
      pdf.text('Grand Total', xPos, yPosition + 4);
      xPos += colWidths[0];
      pdf.text(formatCurrency(grandTotals.total_fob), xPos, yPosition + 4);
      xPos += colWidths[1];
      pdf.text(formatCurrency(grandTotals.total_line_entered_value), xPos, yPosition + 4);
      xPos += colWidths[2];

      pdf.setTextColor(grandTotals.tariff_difference >= 0 ? 16 : 239, grandTotals.tariff_difference >= 0 ? 185 : 68, grandTotals.tariff_difference >= 0 ? 129 : 68);
      pdf.text(formatCurrency(grandTotals.tariff_difference), xPos, yPosition + 4);
      xPos += colWidths[3];

      pdf.setTextColor(grandTotals.duty_difference >= 0 ? 16 : 239, grandTotals.duty_difference >= 0 ? 185 : 68, grandTotals.duty_difference >= 0 ? 129 : 68);
      pdf.text(formatCurrency(grandTotals.duty_difference), xPos, yPosition + 4);
      xPos += colWidths[4];

      pdf.setTextColor(grandTotals.freight_difference >= 0 ? 16 : 239, grandTotals.freight_difference >= 0 ? 185 : 68, grandTotals.freight_difference >= 0 ? 129 : 68);
      pdf.text(formatCurrency(grandTotals.freight_difference), xPos, yPosition + 4);
      xPos += colWidths[5];

      pdf.setTextColor(grandTotal >= 0 ? 16 : 239, grandTotal >= 0 ? 185 : 68, grandTotal >= 0 ? 129 : 68);
      pdf.text(formatCurrency(grandTotal), xPos, yPosition + 4);

      yPosition += 8;

      // ===== CUSTOMER TABLE (Page 2) =====
      if (customerPivotData.length > 0) {
        pdf.addPage({ orientation: 'landscape' });
        addHeader();
        yPosition = 24;

        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 41, 59);
        pdf.text('By Customer', margin, yPosition);
        yPosition += 4;

        // Customer table headers
        const custHeaders = ['Customer', 'Total FOB', 'Entered Value', 'Tariff Var', 'Duty Var', 'Freight Var', 'Total Variance', '# Entries'];
        const custColWidths = [55, 30, 30, 28, 28, 28, 30, 20];

        pdf.setFillColor(241, 245, 249);
        pdf.rect(margin, yPosition, tableWidth, 6, 'F');
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(71, 85, 105);

        let cxPos = margin + 2;
        custHeaders.forEach((header, i) => {
          pdf.text(header, cxPos, yPosition + 4);
          cxPos += custColWidths[i];
        });
        yPosition += 6;

        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');

        customerPivotData.forEach((row, idx) => {
          if (yPosition > pageHeight - 15) {
            addFooter();
            pdf.addPage({ orientation: 'landscape' });
            addHeader();
            yPosition = 24;
          }

          if (idx % 2 === 0) {
            pdf.setFillColor(249, 250, 251);
            pdf.rect(margin, yPosition, tableWidth, 5, 'F');
          }

          const totalVar = row.tariff_difference + row.duty_difference + row.freight_difference;

          cxPos = margin + 2;
          pdf.setTextColor(30, 41, 59);
          pdf.text(String(row.customer || 'Unknown').substring(0, 25), cxPos, yPosition + 3.5);
          cxPos += custColWidths[0];
          pdf.text(formatCurrency(row.total_fob), cxPos, yPosition + 3.5);
          cxPos += custColWidths[1];
          pdf.text(formatCurrency(row.total_line_entered_value), cxPos, yPosition + 3.5);
          cxPos += custColWidths[2];

          pdf.setTextColor(row.tariff_difference >= 0 ? 16 : 239, row.tariff_difference >= 0 ? 185 : 68, row.tariff_difference >= 0 ? 129 : 68);
          pdf.text(formatCurrency(row.tariff_difference), cxPos, yPosition + 3.5);
          cxPos += custColWidths[3];

          pdf.setTextColor(row.duty_difference >= 0 ? 16 : 239, row.duty_difference >= 0 ? 185 : 68, row.duty_difference >= 0 ? 129 : 68);
          pdf.text(formatCurrency(row.duty_difference), cxPos, yPosition + 3.5);
          cxPos += custColWidths[4];

          pdf.setTextColor(row.freight_difference >= 0 ? 16 : 239, row.freight_difference >= 0 ? 185 : 68, row.freight_difference >= 0 ? 129 : 68);
          pdf.text(formatCurrency(row.freight_difference), cxPos, yPosition + 3.5);
          cxPos += custColWidths[5];

          pdf.setTextColor(totalVar >= 0 ? 16 : 239, totalVar >= 0 ? 185 : 68, totalVar >= 0 ? 129 : 68);
          pdf.setFont('helvetica', 'bold');
          pdf.text(formatCurrency(totalVar), cxPos, yPosition + 3.5);
          cxPos += custColWidths[6];

          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(30, 41, 59);
          pdf.text(String(row.count), cxPos, yPosition + 3.5);

          yPosition += 5;
        });

        // Customer grand totals row
        const custGrandTotals = customerPivotData.reduce((acc, r) => ({
          total_fob: acc.total_fob + r.total_fob,
          total_line_entered_value: acc.total_line_entered_value + r.total_line_entered_value,
          tariff_difference: acc.tariff_difference + r.tariff_difference,
          duty_difference: acc.duty_difference + r.duty_difference,
          freight_difference: acc.freight_difference + r.freight_difference,
          count: acc.count + r.count
        }), { total_fob: 0, total_line_entered_value: 0, tariff_difference: 0, duty_difference: 0, freight_difference: 0, count: 0 });

        const custGrandTotal = custGrandTotals.tariff_difference + custGrandTotals.duty_difference + custGrandTotals.freight_difference;

        pdf.setFillColor(226, 232, 240);
        pdf.rect(margin, yPosition, tableWidth, 5.5, 'F');
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(7);
        pdf.setTextColor(30, 41, 59);

        cxPos = margin + 2;
        pdf.text('Grand Total', cxPos, yPosition + 4);
        cxPos += custColWidths[0];
        pdf.text(formatCurrency(custGrandTotals.total_fob), cxPos, yPosition + 4);
        cxPos += custColWidths[1];
        pdf.text(formatCurrency(custGrandTotals.total_line_entered_value), cxPos, yPosition + 4);
        cxPos += custColWidths[2];

        pdf.setTextColor(custGrandTotals.tariff_difference >= 0 ? 16 : 239, custGrandTotals.tariff_difference >= 0 ? 185 : 68, custGrandTotals.tariff_difference >= 0 ? 129 : 68);
        pdf.text(formatCurrency(custGrandTotals.tariff_difference), cxPos, yPosition + 4);
        cxPos += custColWidths[3];

        pdf.setTextColor(custGrandTotals.duty_difference >= 0 ? 16 : 239, custGrandTotals.duty_difference >= 0 ? 185 : 68, custGrandTotals.duty_difference >= 0 ? 129 : 68);
        pdf.text(formatCurrency(custGrandTotals.duty_difference), cxPos, yPosition + 4);
        cxPos += custColWidths[4];

        pdf.setTextColor(custGrandTotals.freight_difference >= 0 ? 16 : 239, custGrandTotals.freight_difference >= 0 ? 185 : 68, custGrandTotals.freight_difference >= 0 ? 129 : 68);
        pdf.text(formatCurrency(custGrandTotals.freight_difference), cxPos, yPosition + 4);
        cxPos += custColWidths[5];

        pdf.setTextColor(custGrandTotal >= 0 ? 16 : 239, custGrandTotal >= 0 ? 185 : 68, custGrandTotal >= 0 ? 129 : 68);
        pdf.text(formatCurrency(custGrandTotal), cxPos, yPosition + 4);
        cxPos += custColWidths[6];

        pdf.setTextColor(30, 41, 59);
        pdf.text(String(custGrandTotals.count), cxPos, yPosition + 4);

        yPosition += 8;
        addFooter();
      }

      // ===== CHARTS (Below Pivot Table) =====
      // Capture charts using html2canvas
      if (chartsRef.current) {
        try {
          const chartsCanvas = await html2canvas(chartsRef.current, {
            scale: 2,
            backgroundColor: '#ffffff',
            logging: false,
            useCORS: true
          });

          const chartsImg = chartsCanvas.toDataURL('image/png');

          // Calculate chart dimensions to fit remaining space
          const availableChartHeight = pageHeight - yPosition - 10;
          const availableChartWidth = pageWidth - margin * 2;

          // Scale to fit available space
          const imgAspectRatio = chartsCanvas.width / chartsCanvas.height;
          let finalWidth = availableChartWidth;
          let finalHeight = finalWidth / imgAspectRatio;

          // If too tall, scale by height instead
          if (finalHeight > availableChartHeight) {
            finalHeight = availableChartHeight;
            finalWidth = finalHeight * imgAspectRatio;
          }

          // Center the charts horizontally
          const chartX = margin + (availableChartWidth - finalWidth) / 2;

          pdf.addImage(chartsImg, 'PNG', chartX, yPosition, finalWidth, finalHeight);
        } catch (chartError) {
          console.warn('Could not capture charts:', chartError);
          pdf.setFontSize(8);
          pdf.setTextColor(150, 150, 150);
          pdf.text('Charts could not be captured.', margin, yPosition + 10);
        }
      }

      // Add footer
      addFooter();

      // Save the PDF
      pdf.save(`Cost_Allocation_Report_${startDate}_to_${endDate}.pdf`);

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setExportingPdf(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    const absValue = Math.abs(value);
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(absValue);
    return value < 0 ? `(${formatted})` : formatted;
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-US').format(Math.round(value));
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900 mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color || entry.fill }} className="text-sm">
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div ref={containerRef} className={embedded ? "bg-slate-50 p-4" : "p-6 bg-slate-50 min-h-screen"}>
      {/* Header */}
      <div className="mb-6">
        {!embedded && (
          <button
            onClick={onBackToDashboard}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft size={16} />
            Back to Dashboard
          </button>
        )}
        <div className="flex items-center justify-between">
          {!embedded && (
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-2">Cost Allocation Report</h1>
              <p className="text-sm text-gray-600">Analyze cost variances by month, country, and customer</p>
            </div>
          )}
          <div className="flex items-center gap-2">
            {/* PDF Export Button */}
            {data.length > 0 && (
              <button
                onClick={exportToPdf}
                disabled={exportingPdf}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exportingPdf ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <FileText size={16} />
                    Export PDF
                  </>
                )}
              </button>
            )}
            <div className="w-px h-6 bg-gray-300 mx-1"></div>
            <button
              onClick={() => setShowPivot(!showPivot)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${showPivot ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}
            >
              Pivot
            </button>
            <button
              onClick={() => setShowCharts(!showCharts)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${showCharts ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}
            >
              <BarChart3 size={16} />
              Charts
            </button>
            <button
              onClick={() => setShowDetail(!showDetail)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${showDetail ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}
            >
              Detail
            </button>
          </div>
        </div>
      </div>

      {/* Filters Card - Date Range + Post-load Filters */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Filter size={18} className="text-gray-600" />
          <h2 className="text-lg font-medium text-gray-900">Filters</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              <Calendar size={14} className="inline mr-1" />
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              <Calendar size={14} className="inline mr-1" />
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            />
          </div>

          <MultiSelectDropdown
            label="Division"
            icon={Building2}
            options={dropdownOptions.divisions}
            selected={postFilters.division}
            filterType="division"
            filterGroup="post"
          />

          <MultiSelectDropdown
            label="Customer"
            icon={Users}
            options={dropdownOptions.customers}
            selected={postFilters.customer}
            filterType="customer"
            filterGroup="post"
          />

          <MultiSelectDropdown
            label="Country"
            icon={Globe}
            options={dropdownOptions.countries}
            selected={postFilters.country}
            filterType="country"
            filterGroup="post"
          />

          <MultiSelectDropdown
            label="Status"
            icon={CheckCircle}
            options={dropdownOptions.statuses}
            selected={postFilters.status}
            filterType="status"
            filterGroup="post"
          />
        </div>

        <div className="flex gap-3 mt-4">
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            {loading ? 'Loading...' : 'Load Data'}
          </button>

          {selectedMonth && (
            <button
              onClick={() => setSelectedMonth(null)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300"
            >
              Clear Month Filter: {selectedMonth}
            </button>
          )}

          {selectedCustomer && (
            <button
              onClick={() => setSelectedCustomer(null)}
              className="flex items-center gap-2 px-4 py-2 bg-violet-200 text-violet-700 text-sm font-medium rounded-lg hover:bg-violet-300"
            >
              Clear Customer Filter: {selectedCustomer}
            </button>
          )}

          {(postFilters.division.length > 0 || postFilters.customer.length > 0 || postFilters.status.length > 0 || postFilters.country.length > 0) && (
            <button
              onClick={() => setPostFilters({ division: [], customer: [], status: [], country: [] })}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-200"
            >
              Clear All Filters
            </button>
          )}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-2">
            <AlertCircle size={20} className="text-red-600" />
            <p className="text-sm font-medium text-red-900">{error}</p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {/* Summary Stats - Total FOB, Duty Saving, Tariff Saving, Freight Saving */}
      {!loading && data.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-blue-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign size={16} className="text-blue-600" />
              <span className="text-xs font-medium text-blue-900">Total FOB</span>
            </div>
            <p className="text-2xl font-bold text-blue-900">{formatCurrency(stats.totalFob)}</p>
          </div>

          <div className="bg-white border border-red-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              {stats.dutySaving >= 0 ? <TrendingUp size={16} className="text-green-600" /> : <TrendingDown size={16} className="text-red-600" />}
              <span className="text-xs font-medium text-gray-900">Duty Variance</span>
            </div>
            <p className={`text-2xl font-bold ${stats.dutySaving >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(stats.dutySaving)}
            </p>
          </div>

          <div className="bg-white border border-orange-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              {stats.tariffSaving >= 0 ? <TrendingUp size={16} className="text-green-600" /> : <TrendingDown size={16} className="text-red-600" />}
              <span className="text-xs font-medium text-gray-900">Tariff Variance</span>
            </div>
            <p className={`text-2xl font-bold ${stats.tariffSaving >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(stats.tariffSaving)}
            </p>
          </div>

          <div className="bg-white border border-purple-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              {stats.freightSaving >= 0 ? <TrendingUp size={16} className="text-green-600" /> : <TrendingDown size={16} className="text-red-600" />}
              <span className="text-xs font-medium text-gray-900">Freight Variance</span>
            </div>
            <p className={`text-2xl font-bold ${stats.freightSaving >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(stats.freightSaving)}
            </p>
          </div>

          {/* Data Completeness Summary */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm col-span-2 md:col-span-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-green-600" />
                  <span className="text-xs font-medium text-gray-700">Data Completeness</span>
                </div>
                <div className="h-4 w-px bg-gray-300"></div>
                <span className="text-sm font-bold text-green-600">{completenessStats.completeInvoices} Complete</span>
                <span className="text-sm text-gray-500">of {completenessStats.totalInvoices} invoices</span>
                <span className={`text-sm font-medium ${completenessStats.completionRate >= 80 ? 'text-green-600' : completenessStats.completionRate >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                  ({completenessStats.completionRate}%)
                </span>
              </div>
              {completenessStats.incompleteInvoices > 0 && (
                <div className="flex items-center gap-2">
                  <AlertTriangle size={14} className="text-yellow-500" />
                  <span className="text-xs text-yellow-700">{completenessStats.incompleteInvoices} invoices with missing data</span>
                </div>
              )}
              <button
                onClick={() => { setShowMissingHTSModal(true); fetchMissingHTSData(); }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-orange-100 text-orange-700 border border-orange-200 hover:bg-orange-200 transition-colors"
              >
                <Search size={12} />
                Missing HTS Data
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pivot Table Section (BEFORE Charts) */}
      {!loading && data.length > 0 && showPivot && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6 shadow-sm">
          <div className="bg-gray-50 border-b border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900">📋 Monthly Summary</h3>
              <button
                onClick={exportPivotToExcel}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700"
              >
                <Download size={14} />
                Export Excel
              </button>
            </div>

            {/* Pivot Filter Dropdowns - Multi-select */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setOpenDropdown(openDropdown === 'pivot-customer' ? null : 'pivot-customer')}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between"
                >
                  <span className={pivotFilters.customer.length === 0 ? 'text-gray-400' : 'text-gray-700'}>
                    {pivotFilters.customer.length === 0 ? 'All Customers' : `${pivotFilters.customer.length} Customers`}
                  </span>
                  <ChevronDown size={12} className="text-gray-400" />
                </button>
                {openDropdown === 'pivot-customer' && (
                  <div className="absolute z-50 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    <div className="sticky top-0 bg-gray-50 border-b px-2 py-1.5 flex justify-between">
                      <span className="text-xs text-gray-500">{pivotFilters.customer.length} selected</span>
                      <button onClick={() => clearFilter('customer', 'pivot')} className="text-xs text-blue-600">Clear</button>
                    </div>
                    {dropdownOptions.customers.map(c => (
                      <label key={c} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 cursor-pointer text-xs">
                        <input type="checkbox" checked={pivotFilters.customer.includes(c)} onChange={() => toggleMultiSelect('customer', 'pivot', c)} className="w-3 h-3" />
                        <span className="truncate">{c}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative">
                <button
                  type="button"
                  onClick={() => setOpenDropdown(openDropdown === 'pivot-office' ? null : 'pivot-office')}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between"
                >
                  <span className={pivotFilters.office.length === 0 ? 'text-gray-400' : 'text-gray-700'}>
                    {pivotFilters.office.length === 0 ? 'All Offices' : `${pivotFilters.office.length} Offices`}
                  </span>
                  <ChevronDown size={12} className="text-gray-400" />
                </button>
                {openDropdown === 'pivot-office' && (
                  <div className="absolute z-50 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    <div className="sticky top-0 bg-gray-50 border-b px-2 py-1.5 flex justify-between">
                      <span className="text-xs text-gray-500">{pivotFilters.office.length} selected</span>
                      <button onClick={() => clearFilter('office', 'pivot')} className="text-xs text-blue-600">Clear</button>
                    </div>
                    {dropdownOptions.offices.map(o => (
                      <label key={o} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 cursor-pointer text-xs">
                        <input type="checkbox" checked={pivotFilters.office.includes(o)} onChange={() => toggleMultiSelect('office', 'pivot', o)} className="w-3 h-3" />
                        <span className="truncate">{o}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative">
                <button
                  type="button"
                  onClick={() => setOpenDropdown(openDropdown === 'pivot-country' ? null : 'pivot-country')}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between"
                >
                  <span className={pivotFilters.country.length === 0 ? 'text-gray-400' : 'text-gray-700'}>
                    {pivotFilters.country.length === 0 ? 'All Countries' : `${pivotFilters.country.length} Countries`}
                  </span>
                  <ChevronDown size={12} className="text-gray-400" />
                </button>
                {openDropdown === 'pivot-country' && (
                  <div className="absolute z-50 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    <div className="sticky top-0 bg-gray-50 border-b px-2 py-1.5 flex justify-between">
                      <span className="text-xs text-gray-500">{pivotFilters.country.length} selected</span>
                      <button onClick={() => clearFilter('country', 'pivot')} className="text-xs text-blue-600">Clear</button>
                    </div>
                    {dropdownOptions.countries.map(c => (
                      <label key={c} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 cursor-pointer text-xs">
                        <input type="checkbox" checked={pivotFilters.country.includes(c)} onChange={() => toggleMultiSelect('country', 'pivot', c)} className="w-3 h-3" />
                        <span className="truncate">{c}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative">
                <button
                  type="button"
                  onClick={() => setOpenDropdown(openDropdown === 'pivot-terms' ? null : 'pivot-terms')}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between"
                >
                  <span className={pivotFilters.terms.length === 0 ? 'text-gray-400' : 'text-gray-700'}>
                    {pivotFilters.terms.length === 0 ? 'All Terms' : `${pivotFilters.terms.length} Terms`}
                  </span>
                  <ChevronDown size={12} className="text-gray-400" />
                </button>
                {openDropdown === 'pivot-terms' && (
                  <div className="absolute z-50 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    <div className="sticky top-0 bg-gray-50 border-b px-2 py-1.5 flex justify-between">
                      <span className="text-xs text-gray-500">{pivotFilters.terms.length} selected</span>
                      <button onClick={() => clearFilter('terms', 'pivot')} className="text-xs text-blue-600">Clear</button>
                    </div>
                    {dropdownOptions.terms.map(t => (
                      <label key={t} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 cursor-pointer text-xs">
                        <input type="checkbox" checked={pivotFilters.terms.includes(t)} onChange={() => toggleMultiSelect('terms', 'pivot', t)} className="w-3 h-3" />
                        <span className="truncate">{t}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative">
                <button
                  type="button"
                  onClick={() => setOpenDropdown(openDropdown === 'pivot-division' ? null : 'pivot-division')}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between"
                >
                  <span className={pivotFilters.division.length === 0 ? 'text-gray-400' : 'text-gray-700'}>
                    {pivotFilters.division.length === 0 ? 'All Divisions' : `${pivotFilters.division.length} Divisions`}
                  </span>
                  <ChevronDown size={12} className="text-gray-400" />
                </button>
                {openDropdown === 'pivot-division' && (
                  <div className="absolute z-50 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    <div className="sticky top-0 bg-gray-50 border-b px-2 py-1.5 flex justify-between">
                      <span className="text-xs text-gray-500">{pivotFilters.division.length} selected</span>
                      <button onClick={() => clearFilter('division', 'pivot')} className="text-xs text-blue-600">Clear</button>
                    </div>
                    {dropdownOptions.divisions.map(d => (
                      <label key={d} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 cursor-pointer text-xs">
                        <input type="checkbox" checked={pivotFilters.division.includes(d)} onChange={() => toggleMultiSelect('division', 'pivot', d)} className="w-3 h-3" />
                        <span className="truncate">{d}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Month</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Sum of FOB</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Sum of Custom Value</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Tariff Diff</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Duty Diff</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Freight Diff</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700 bg-slate-200">Total Variance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pivotData.map((row) => {
                  const totalVariance = row.tariff_difference + row.duty_difference + row.freight_difference;
                  return (
                    <tr
                      key={row.month}
                      className={`hover:bg-blue-50 cursor-pointer transition-colors ${selectedMonth === row.month ? 'bg-blue-100' : ''}`}
                      onClick={() => setSelectedMonth(selectedMonth === row.month ? null : row.month)}
                    >
                      <td className="px-4 py-3 font-medium text-gray-900 flex items-center gap-2">
                        {selectedMonth === row.month ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        {row.month}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(row.total_fob)}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(row.total_line_entered_value)}</td>
                      <td className={`px-4 py-3 text-right font-medium ${row.tariff_difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(row.tariff_difference)}
                      </td>
                      <td className={`px-4 py-3 text-right font-medium ${row.duty_difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(row.duty_difference)}
                      </td>
                      <td className={`px-4 py-3 text-right font-medium ${row.freight_difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(row.freight_difference)}
                      </td>
                      <td className={`px-4 py-3 text-right font-bold bg-slate-50 ${totalVariance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(totalVariance)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-slate-200 border-t-2 border-gray-300">
                <tr>
                  <td className="px-4 py-3 font-bold text-gray-900">Grand Total</td>
                  <td className="px-4 py-3 text-right font-bold text-gray-900">{formatCurrency(grandTotals.total_fob)}</td>
                  <td className="px-4 py-3 text-right font-bold text-gray-900">{formatCurrency(grandTotals.total_line_entered_value)}</td>
                  <td className={`px-4 py-3 text-right font-bold ${grandTotals.tariff_difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(grandTotals.tariff_difference)}
                  </td>
                  <td className={`px-4 py-3 text-right font-bold ${grandTotals.duty_difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(grandTotals.duty_difference)}
                  </td>
                  <td className={`px-4 py-3 text-right font-bold ${grandTotals.freight_difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(grandTotals.freight_difference)}
                  </td>
                  <td className={`px-4 py-3 text-right font-bold bg-slate-300 ${(grandTotals.tariff_difference + grandTotals.duty_difference + grandTotals.freight_difference) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(grandTotals.tariff_difference + grandTotals.duty_difference + grandTotals.freight_difference)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Customer Pivot Table */}
      {!loading && data.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-6">
          <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-violet-50 to-white">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Users size={18} className="text-violet-600" />
                By Customer
                {selectedMonth && <span className="text-sm font-normal text-blue-600 ml-2">- {selectedMonth}</span>}
              </h2>
              <span className="text-sm text-gray-500">{customerPivotData.length} customers</span>
            </div>
          </div>

          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 border-b border-gray-200 sticky top-0 z-10">
                <tr>
                  <th
                    className="px-4 py-3 text-left font-semibold text-gray-700 cursor-pointer hover:bg-slate-200 transition-colors select-none"
                    onClick={() => setCustomerSort(prev => ({ key: 'customer', direction: prev.key === 'customer' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                  >
                    <div className="flex items-center gap-1">
                      Customer
                      {customerSort.key === 'customer' && (
                        <span className="text-violet-600">{customerSort.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-right font-semibold text-gray-700 cursor-pointer hover:bg-slate-200 transition-colors select-none"
                    onClick={() => setCustomerSort(prev => ({ key: 'count', direction: prev.key === 'count' && prev.direction === 'desc' ? 'asc' : 'desc' }))}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Records
                      {customerSort.key === 'count' && (
                        <span className="text-violet-600">{customerSort.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-right font-semibold text-gray-700 cursor-pointer hover:bg-slate-200 transition-colors select-none"
                    onClick={() => setCustomerSort(prev => ({ key: 'total_fob', direction: prev.key === 'total_fob' && prev.direction === 'desc' ? 'asc' : 'desc' }))}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Sum of FOB
                      {customerSort.key === 'total_fob' && (
                        <span className="text-violet-600">{customerSort.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-right font-semibold text-gray-700 cursor-pointer hover:bg-slate-200 transition-colors select-none"
                    onClick={() => setCustomerSort(prev => ({ key: 'total_line_entered_value', direction: prev.key === 'total_line_entered_value' && prev.direction === 'desc' ? 'asc' : 'desc' }))}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Custom Value
                      {customerSort.key === 'total_line_entered_value' && (
                        <span className="text-violet-600">{customerSort.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-right font-semibold text-gray-700 cursor-pointer hover:bg-slate-200 transition-colors select-none"
                    onClick={() => setCustomerSort(prev => ({ key: 'tariff_difference', direction: prev.key === 'tariff_difference' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Tariff Diff
                      {customerSort.key === 'tariff_difference' && (
                        <span className="text-violet-600">{customerSort.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-right font-semibold text-gray-700 cursor-pointer hover:bg-slate-200 transition-colors select-none"
                    onClick={() => setCustomerSort(prev => ({ key: 'duty_difference', direction: prev.key === 'duty_difference' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Duty Diff
                      {customerSort.key === 'duty_difference' && (
                        <span className="text-violet-600">{customerSort.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-right font-semibold text-gray-700 cursor-pointer hover:bg-slate-200 transition-colors select-none"
                    onClick={() => setCustomerSort(prev => ({ key: 'freight_difference', direction: prev.key === 'freight_difference' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Freight Diff
                      {customerSort.key === 'freight_difference' && (
                        <span className="text-violet-600">{customerSort.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-right font-semibold text-gray-700 bg-slate-200 cursor-pointer hover:bg-slate-300 transition-colors select-none"
                    onClick={() => setCustomerSort(prev => ({ key: 'total_variance', direction: prev.key === 'total_variance' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Total Variance
                      {customerSort.key === 'total_variance' && (
                        <span className="text-violet-600">{customerSort.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {customerPivotData.map((row) => {
                  const totalVariance = row.tariff_difference + row.duty_difference + row.freight_difference;
                  return (
                    <tr
                      key={row.customer}
                      className={`hover:bg-violet-50 transition-colors cursor-pointer ${selectedCustomer === row.customer ? 'bg-violet-100 ring-1 ring-violet-400' : ''}`}
                      onClick={() => setSelectedCustomer(prev => prev === row.customer ? null : row.customer)}
                    >
                      <td className="px-4 py-3 font-medium text-gray-900">{row.customer}</td>
                      <td className="px-4 py-3 text-right text-gray-600">{row.count.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(row.total_fob)}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(row.total_line_entered_value)}</td>
                      <td className={`px-4 py-3 text-right font-medium ${row.tariff_difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(row.tariff_difference)}
                      </td>
                      <td className={`px-4 py-3 text-right font-medium ${row.duty_difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(row.duty_difference)}
                      </td>
                      <td className={`px-4 py-3 text-right font-medium ${row.freight_difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(row.freight_difference)}
                      </td>
                      <td className={`px-4 py-3 text-right font-bold bg-slate-50 ${totalVariance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(totalVariance)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-slate-200 border-t-2 border-gray-300 sticky bottom-0">
                <tr>
                  <td className="px-4 py-3 font-bold text-gray-900">Grand Total</td>
                  <td className="px-4 py-3 text-right font-bold text-gray-900">{grandTotals.count.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-bold text-gray-900">{formatCurrency(grandTotals.total_fob)}</td>
                  <td className="px-4 py-3 text-right font-bold text-gray-900">{formatCurrency(grandTotals.total_line_entered_value)}</td>
                  <td className={`px-4 py-3 text-right font-bold ${grandTotals.tariff_difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(grandTotals.tariff_difference)}
                  </td>
                  <td className={`px-4 py-3 text-right font-bold ${grandTotals.duty_difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(grandTotals.duty_difference)}
                  </td>
                  <td className={`px-4 py-3 text-right font-bold ${grandTotals.freight_difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(grandTotals.freight_difference)}
                  </td>
                  <td className={`px-4 py-3 text-right font-bold bg-slate-300 ${(grandTotals.tariff_difference + grandTotals.duty_difference + grandTotals.freight_difference) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(grandTotals.tariff_difference + grandTotals.duty_difference + grandTotals.freight_difference)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Charts Section */}
      {!loading && data.length > 0 && showCharts && (
        <div ref={chartsRef} className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* FOB by Customer Bar Chart (Clickable) */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Users size={16} className="text-blue-600" />
              FOB by Customer
              {selectedCustomer && <span className="text-xs font-normal text-blue-600 ml-1">(click to clear)</span>}
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={fobByCustomerData} layout="vertical" margin={{ left: 5, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" tick={{ fontSize: 9 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={80} />
                <Tooltip formatter={(value) => formatCurrency(value)} labelFormatter={(label) => {
                  const item = fobByCustomerData.find(d => d.name === label);
                  return item ? item.fullName : label;
                }} />
                <Bar
                  dataKey="value"
                  name="Total FOB"
                  radius={[0, 4, 4, 0]}
                  cursor="pointer"
                  onClick={(data) => {
                    if (data && data.fullName) {
                      setSelectedCustomer(prev => prev === data.fullName ? null : data.fullName);
                    }
                  }}
                >
                  {fobByCustomerData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={selectedCustomer === entry.fullName ? '#2563eb' : COLORS[index % COLORS.length]}
                      opacity={selectedCustomer && selectedCustomer !== entry.fullName ? 0.4 : 1}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Variance Stacked Bar Chart (by Month) */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <BarChart3 size={16} className="text-orange-600" />
              Variance by Month (Stacked)
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={varianceStackedData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Duty Diff" stackId="a" fill={VARIANCE_COLORS.duty} />
                <Bar dataKey="Tariff Diff" stackId="a" fill={VARIANCE_COLORS.tariff} />
                <Bar dataKey="Freight Diff" stackId="a" fill={VARIANCE_COLORS.freight} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Customer Impact */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Users size={16} className="text-purple-600" />
              Variance Impact by Customer
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={customerImpactData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={90} />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="value"
                  name="Total Variance"
                  fill="#8b5cf6"
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Hierarchical Detail Table (Invoice → PO → Style) */}
      {!loading && data.length > 0 && showDetail && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          <div className="bg-gray-50 border-b border-gray-200 p-4 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">
              📄 Detail Data ({hierarchicalData.length} invoices)
              {selectedMonth && <span className="ml-2 text-blue-600">- {selectedMonth}</span>}
              {selectedCustomer && <span className="ml-2 text-violet-600">- {selectedCustomer}</span>}
            </h3>
            <button
              onClick={exportDetailToExcel}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700"
            >
              <Download size={14} />
              Export Excel
            </button>
          </div>

          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-100 border-b border-gray-200 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 w-8"></th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Invoice / PO / Style</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Customer</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Division</th>
                  <th className="px-3 py-2 text-center font-medium text-gray-700">Status</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Color</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Country</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-700">Qty</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-700">FOB</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-700">Duty Diff</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-700">Tariff Diff</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-700">Freight Diff</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {hierarchicalData.slice(0, 100).map((inv) => (
                  <React.Fragment key={inv.invoice}>
                    {/* Invoice Row */}
                    <tr
                      className={`hover:bg-blue-100 cursor-pointer font-medium ${inv.completeness.isComplete ? 'bg-green-50' : 'bg-yellow-50'}`}
                      onClick={() => toggleInvoice(inv.invoice)}
                    >
                      <td className="px-3 py-2">
                        {expandedInvoices.has(inv.invoice) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </td>
                      <td className="px-3 py-2 text-blue-900">{inv.invoice}</td>
                      <td className="px-3 py-2 text-gray-700 text-xs">{inv.customer}</td>
                      <td className="px-3 py-2 text-gray-700 text-xs">{inv.division}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-center gap-1">
                          {inv.completeness.isComplete ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-[10px] font-medium">
                              <CheckCircle size={10} /> Complete
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-[10px] font-medium">
                              <AlertTriangle size={10} /> {inv.completeness.statuses.length} Issues
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2"></td>
                      <td className="px-3 py-2"></td>
                      <td className="px-3 py-2 text-right">{formatNumber(inv.totals.qty)}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(inv.totals.fob)}</td>
                      <td className={`px-3 py-2 text-right ${inv.totals.duty >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(inv.totals.duty)}
                      </td>
                      <td className={`px-3 py-2 text-right ${inv.totals.tariff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(inv.totals.tariff)}
                      </td>
                      <td className={`px-3 py-2 text-right ${inv.totals.freight >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(inv.totals.freight)}
                      </td>
                    </tr>

                    {/* Completeness Detail Row (shows when expanded) */}
                    {expandedInvoices.has(inv.invoice) && !inv.completeness.isComplete && (
                      <tr className="bg-yellow-50 border-l-4 border-yellow-400">
                        <td colSpan={12} className="px-6 py-3">
                          <div className="flex flex-wrap gap-4 text-xs">
                            <div className="font-medium text-gray-700">Data Completeness:</div>
                            <div className="flex items-center gap-1">
                              {inv.completeness.hasDuty ? <CheckCircle size={12} className="text-green-500" /> : <XCircle size={12} className="text-red-500" />}
                              <span className={inv.completeness.hasDuty ? 'text-green-700' : 'text-red-700'}>Duty</span>
                            </div>
                            <div className="flex items-center gap-1">
                              {inv.completeness.hasFreight ? <CheckCircle size={12} className="text-green-500" /> : <XCircle size={12} className="text-red-500" />}
                              <span className={inv.completeness.hasFreight ? 'text-green-700' : 'text-red-700'}>Freight</span>
                            </div>
                            <div className="flex items-center gap-1">
                              {inv.completeness.hasHTS ? <CheckCircle size={12} className="text-green-500" /> : <XCircle size={12} className="text-red-500" />}
                              <span className={inv.completeness.hasHTS ? 'text-green-700' : 'text-red-700'}>HTS Data</span>
                            </div>
                            <div className="flex items-center gap-1">
                              {inv.completeness.hasShipping ? <CheckCircle size={12} className="text-green-500" /> : <XCircle size={12} className="text-red-500" />}
                              <span className={inv.completeness.hasShipping ? 'text-green-700' : 'text-red-700'}>Shipping</span>
                            </div>
                            {inv.completeness.statuses.length > 0 && (
                              <div className="flex items-center gap-1 ml-4 text-gray-500">
                                <Info size={12} />
                                {inv.completeness.statuses.join(', ')}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}

                    {/* PO Rows */}
                    {expandedInvoices.has(inv.invoice) && inv.pos.map((po) => (
                      <React.Fragment key={`${inv.invoice}-${po.po}`}>
                        <tr
                          className="bg-gray-50 hover:bg-gray-100 cursor-pointer"
                          onClick={() => togglePO(`${inv.invoice}-${po.po}`)}
                        >
                          <td className="px-3 py-2 pl-6">
                            {expandedPOs.has(`${inv.invoice}-${po.po}`) ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                          </td>
                          <td className="px-3 py-2 text-gray-700 pl-4">↳ PO: {po.po}</td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2 text-right text-gray-600">{formatNumber(po.totals.qty)}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{formatCurrency(po.totals.fob)}</td>
                          <td className={`px-3 py-2 text-right ${po.totals.duty >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(po.totals.duty)}
                          </td>
                          <td className={`px-3 py-2 text-right ${po.totals.tariff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(po.totals.tariff)}
                          </td>
                          <td className={`px-3 py-2 text-right ${po.totals.freight >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(po.totals.freight)}
                          </td>
                        </tr>

                        {/* Style Rows */}
                        {expandedPOs.has(`${inv.invoice}-${po.po}`) && po.styles.map((style, idx) => (
                          <tr key={`${inv.invoice}-${po.po}-${idx}`} className="hover:bg-gray-50">
                            <td className="px-3 py-1.5"></td>
                            <td className="px-3 py-1.5 text-gray-500 pl-10">↳ {style.style || '-'}</td>
                            <td className="px-3 py-1.5"></td>
                            <td className="px-3 py-1.5"></td>
                            <td className="px-3 py-1.5">
                              {style.status === 'COMPLETE DATA' ? (
                                <CheckCircle size={10} className="text-green-500 mx-auto" />
                              ) : (
                                <AlertTriangle size={10} className="text-yellow-500 mx-auto" title={style.status} />
                              )}
                            </td>
                            <td className="px-3 py-1.5 text-gray-500">{style.color || '-'}</td>
                            <td className="px-3 py-1.5 text-gray-500">{style.country || '-'}</td>
                            <td className="px-3 py-1.5 text-right text-gray-500">{formatNumber(style.qty)}</td>
                            <td className="px-3 py-1.5 text-right text-gray-500">{formatCurrency(style.fob)}</td>
                            <td className={`px-3 py-1.5 text-right ${style.duty >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                              {formatCurrency(style.duty)}
                            </td>
                            <td className={`px-3 py-1.5 text-right ${style.tariff >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                              {formatCurrency(style.tariff)}
                            </td>
                            <td className={`px-3 py-1.5 text-right ${style.freight >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                              {formatCurrency(style.freight)}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {hierarchicalData.length > 100 && (
            <div className="bg-yellow-50 border-t border-yellow-200 px-4 py-3">
              <p className="text-xs text-yellow-800">Showing first 100 of {hierarchicalData.length} invoices. Export to Excel to see all data.</p>
            </div>
          )}
        </div>
      )}

      {/* No Data State */}
      {!loading && !error && data.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <Package size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600 font-medium">No data found</p>
          <p className="text-sm text-gray-500 mt-1">Click "Load Data" to fetch data for the selected date range</p>
        </div>
      )}

      {/* Missing HTS Data Modal */}
      {showMissingHTSModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-white rounded-t-2xl">
              <div>
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <AlertTriangle size={20} className="text-orange-500" />
                  Missing HTS Data
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">Received POs with no HTS data - excluded from cost allocation view</p>
              </div>
              <button onClick={() => setShowMissingHTSModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X size={18} className="text-gray-500" />
              </button>
            </div>

            {/* Summary Cards */}
            {missingHTSSummary && !missingHTSLoading && (
              <div className="grid grid-cols-3 gap-4 px-6 py-4 bg-gray-50 border-b border-gray-200">
                <div className="bg-white border border-orange-200 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-orange-600">{missingHTSSummary.total_missing_lines || 0}</p>
                  <p className="text-xs text-gray-600 mt-1">PO Lines Missing</p>
                </div>
                <div className="bg-white border border-red-200 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-red-600">{missingHTSSummary.distinct_invoices_affected || 0}</p>
                  <p className="text-xs text-gray-600 mt-1">Invoices Affected</p>
                </div>
                <div className="bg-white border border-amber-200 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-amber-600">{missingHTSSummary.distinct_pos_affected || 0}</p>
                  <p className="text-xs text-gray-600 mt-1">Distinct POs</p>
                </div>
              </div>
            )}

            {/* Content Area */}
            <div className="flex-1 overflow-auto px-6 py-4">
              {missingHTSLoading && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 size={32} className="animate-spin text-orange-500" />
                  <span className="ml-3 text-gray-600">Loading missing HTS data...</span>
                </div>
              )}

              {missingHTSError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <div className="flex items-center gap-2">
                    <AlertCircle size={16} className="text-red-600" />
                    <p className="text-sm text-red-900">{missingHTSError}</p>
                  </div>
                  <button onClick={fetchMissingHTSData} className="mt-2 text-sm text-red-600 hover:text-red-800 underline">Retry</button>
                </div>
              )}

              {!missingHTSLoading && !missingHTSError && missingHTSData.length === 0 && (
                <div className="text-center py-12">
                  <CheckCircle size={48} className="mx-auto text-green-400 mb-4" />
                  <p className="text-gray-600 font-medium">No missing HTS data found</p>
                  <p className="text-sm text-gray-500 mt-1">All received POs have matching HTS records</p>
                </div>
              )}

              {!missingHTSLoading && !missingHTSError && missingHTSData.length > 0 && (
                <div className="overflow-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">PO Number</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Commercial Invoice</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Style</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Color</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Label</th>
                        <th className="px-3 py-2 text-center font-medium text-gray-700">Division</th>
                        <th className="px-3 py-2 text-center font-medium text-gray-700">Warehouse</th>
                        <th className="px-3 py-2 text-center font-medium text-gray-700">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {missingHTSData.map((row, idx) => (
                        <tr key={idx} className="hover:bg-orange-50">
                          <td className="px-3 py-2 font-medium text-gray-900">{row.prod_num}</td>
                          <td className="px-3 py-2 text-blue-700">{row.comm_inv}</td>
                          <td className="px-3 py-2 text-gray-700">{row.sty}</td>
                          <td className="px-3 py-2 text-gray-700">{row.clr}</td>
                          <td className="px-3 py-2 text-gray-500">{row.lbl || '-'}</td>
                          <td className="px-3 py-2 text-center text-gray-700">{row.div}</td>
                          <td className="px-3 py-2 text-center text-gray-700">{row.shp2}</td>
                          <td className="px-3 py-2 text-center text-gray-600">{row.trans_date}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
              <span className="text-xs text-gray-500">Date range: {startDate} to {endDate}</span>
              <div className="flex items-center gap-2">
                <button onClick={fetchMissingHTSData} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-100">
                  <RefreshCw size={12} />
                  Refresh
                </button>
                {missingHTSData.length > 0 && (
                  <button onClick={exportMissingHTSExcel} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-green-600 text-white hover:bg-green-700">
                    <Download size={12} />
                    Export Excel
                  </button>
                )}
                <button onClick={() => setShowMissingHTSModal(false)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-600 text-white hover:bg-gray-700">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
